/**
 * Manual Entry Main Page
 *
 * Orchestrates the complete manual NIB entry workflow.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  useSubmission,
  useCreateSubmission,
  useAddEmployee,
  useUpdateEmployee,
  useRemoveEmployee,
  useGenerateC10,
  useBulkAddEmployees,
} from '@/hooks/nib/useManualSubmission';
import { useAutoSave } from '@/hooks/nib/useAutoSave';
import { useProfileList } from '@/hooks/nib/useEmployeeProfiles';
import { useEmployeeLimit, useBulkLimitCheck } from '@/hooks/useSubscription';
import {
  LimitIndicator,
  LimitBanner,
  UpgradeModal,
} from '@/components/subscription';

import { EmployerInfoForm } from './components/EmployerInfoForm';
import { EmployeeEntryCard } from './components/EmployeeEntryCard';
import { AddEmployeeModal } from './components/AddEmployeeModal';
import { ProfileSelectorModal } from './components/ProfileSelectorModal';
import { ContributionSummary } from './components/ContributionSummary';

import {
  CreateSubmissionRequest,
  AddEmployeeRequest,
  UpdateEmployeeRequest,
  EmployeeCalculation,
} from '@/types/nib/manualEntry';
import { EmployeeProfile } from '@/types/nib/employeeProfile';

export default function ManualEntryPage() {
  const navigate = useNavigate();
  const { submissionId } = useParams<{ submissionId?: string }>();

  // Modal state
  const [isEmployeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [isProfileModalOpen, setProfileModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] =
    useState<EmployeeCalculation | null>(null);

  // Upgrade modal state with context
  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    attemptedAdd: number;
  }>({ open: false, attemptedAdd: 0 });

  const [dismissedBanner, setDismissedBanner] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);

  // Queries & Mutations
  const { data: submission, isLoading, error } = useSubmission(submissionId);
  const createSubmission = useCreateSubmission();
  const addEmployee = useAddEmployee(submissionId || '');
  const updateEmployee = useUpdateEmployee(submissionId || '');
  const removeEmployee = useRemoveEmployee(submissionId || '');
  const generateC10 = useGenerateC10(submissionId || '');
  const bulkAddEmployees = useBulkAddEmployees(submissionId || '');

  // Fetch profiles for bulk add
  const { data: profiles } = useProfileList();

  // Auto-save hook - keeps draft in sync with server
  const { triggerSave, isSaving } = useAutoSave({
    submissionId: submission?.id,
    enabled: submission?.is_draft ?? false,
  });

  // Employee count and limit checking
  const employeeCount = submission?.employees?.length ?? 0;

  const {
    canAddEmployee,
    remaining,
    limit,
    isUnlimited,
    tier,
    nextTierInfo,
    isLoading: limitLoading,
  } = useEmployeeLimit(employeeCount);

  const { data: bulkLimitCheck } = useBulkLimitCheck(
    employeeCount,
    selectedProfiles.length,
    selectedProfiles.length > 0
  );

  const maxSelectableProfiles = isUnlimited ? undefined : remaining;

  // Handlers
  const handleCreateSubmission = async (data: CreateSubmissionRequest) => {
    try {
      const result = await createSubmission.mutateAsync(data);
      toast.success('Submission created!');
      navigate(`/nib-c10/manual-entry/${result.id}`);
    } catch (err) {
      console.error('Failed to create submission:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to create submission: ${message}`);
    }
  };

  const handleAddEmployee = async (data: AddEmployeeRequest) => {
    try {
      // Check if we're editing an existing employee (id > 0)
      if (editingEmployee && editingEmployee.id > 0) {
        const updateData: UpdateEmployeeRequest = {
          ni_number: data.ni_number,
          last_name: data.last_name,
          first_name: data.first_name,
          basic_wages: data.basic_wages,
          gratuity_wages: data.gratuity_wages,
          weeks_worked: data.weeks_worked,
          pay_indicator: data.pay_indicator,
          category: data.category,
        };
        await updateEmployee.mutateAsync({
          employeeId: String(editingEmployee.id),
          data: updateData,
        });
        toast.success('Employee updated!');
      } else {
        await addEmployee.mutateAsync(data);
        toast.success('Employee added!');
      }
      setEmployeeModalOpen(false);
      setEditingEmployee(null);
      triggerSave(); // Trigger auto-save after modification
    } catch (err) {
      console.error('Failed to save employee:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(message || 'Failed to save employee');
    }
  };

  const handleRemoveEmployee = async (employeeId: number) => {
    if (!confirm('Remove this employee?')) return;
    try {
      await removeEmployee.mutateAsync(String(employeeId));
      toast.success('Employee removed');
      triggerSave(); // Trigger auto-save after modification
    } catch (err) {
      console.error('Failed to remove employee:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to remove employee: ${message}`);
    }
  };

  const handleGenerateC10 = async () => {
    try {
      const result = await generateC10.mutateAsync();

      // Check if file content is available
      if (result.file_content) {
        // Trigger download
        const blob = new Blob([result.file_content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.file_name;
        a.click();
        URL.revokeObjectURL(url);

        toast.success(
          `C10 file "${result.file_name}" generated with ${result.record_count} records!`
        );
      } else {
        // File generated on server but content not returned
        // This is a backend limitation - notify user
        toast.success(
          `C10 file "${result.file_name}" generated successfully! Check with administrator for download.`,
          { duration: 5000 }
        );
      }
    } catch (err) {
      console.error('Failed to generate C10:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(message || 'Failed to generate C10');
    }
  };

  const handleEditEmployee = (employee: EmployeeCalculation) => {
    setEditingEmployee(employee);
    setEmployeeModalOpen(true);
  };

  const handleSelectProfile = (profile: EmployeeProfile) => {
    // Pre-fill form with profile data by creating a partial EmployeeCalculation
    // The modal will handle the conversion to form data
    const prefillData: EmployeeCalculation = {
      id: 0, // New employee
      ni_number: profile.ni_number,
      name: `${profile.first_name} ${profile.last_name}`,
      first_name: profile.first_name,
      last_name: profile.last_name,
      basic_wages: 0,
      gratuity_wages: 0,
      weeks_worked: profile.default_weeks,
      pay_indicator: profile.default_pay_indicator,
      category: profile.default_category,
      truncated_wages: 0,
      capped_wages: 0,
      was_capped: false,
      cap_applied: null,
      employee_contribution: 0,
      employer_contribution: 0,
      gratuity_contribution: 0,
      total_contribution: 0,
      is_valid: true,
      warnings: [],
    };
    setEditingEmployee(prefillData);
    setProfileModalOpen(false);
    setEmployeeModalOpen(true);
  };

  const handleAddEmployeeClick = () => {
    if (!canAddEmployee && !isUnlimited) {
      setUpgradeModal({ open: true, attemptedAdd: 1 });
      return;
    }
    setEditingEmployee(null);
    setEmployeeModalOpen(true);
  };

  const handleAddFromProfiles = async () => {
    if (bulkLimitCheck && !bulkLimitCheck.allowed) {
      setUpgradeModal({
        open: true,
        attemptedAdd: selectedProfiles.length,
      });
      return;
    }

    if (!profiles || selectedProfiles.length === 0) {
      return;
    }

    // Filter profiles by selected IDs
    const selectedProfileData = profiles.filter(p =>
      selectedProfiles.includes(String(p.id))
    );

    if (selectedProfileData.length === 0) {
      toast.error('No profiles selected');
      return;
    }

    // Convert profiles to AddEmployeeRequest format
    // Note: Wages are set to $0 - user needs to edit each employee to add wages
    const employees: AddEmployeeRequest[] = selectedProfileData.map(
      profile => ({
        ni_number: profile.ni_number,
        last_name: profile.last_name,
        first_name: profile.first_name,
        basic_wages: 0, // User needs to edit to add wages
        gratuity_wages: 0,
        weeks_worked: profile.default_weeks,
        pay_indicator: profile.default_pay_indicator,
        category: profile.default_category,
        save_as_profile: false, // Already a profile
      })
    );

    try {
      const result = await bulkAddEmployees.mutateAsync({ employees });

      // Close modal and clear selection
      setProfileModalOpen(false);
      setSelectedProfiles([]);

      // Show result
      if (result.failed_count > 0) {
        toast.error(
          `Added ${result.added_count} employees, ${result.failed_count} failed`
        );
      } else {
        toast.success(
          `Added ${result.added_count} employee${result.added_count !== 1 ? 's' : ''}! Click each to add wages.`,
          { duration: 5000 }
        );
      }

      triggerSave();
    } catch (err) {
      console.error('Failed to bulk add employees:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to add employees: ${message}`);
    }
  };

  // Render: No submission yet
  if (!submissionId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          Manual NIB Entry
        </h1>
        <EmployerInfoForm
          onSubmit={handleCreateSubmission}
          isLoading={createSubmission.isPending}
        />
      </div>
    );
  }

  // Render: Loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <svg
            className="animate-spin h-10 w-10 text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-500">Loading submission...</p>
        </div>
      </div>
    );
  }

  // Render: Error
  if (error || !submission) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h3 className="font-medium text-red-800">
                Failed to load submission
              </h3>
              <p className="text-sm text-red-700 mt-1">
                {error?.message || 'An unexpected error occurred'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isHospitality = submission.format_type === 'HOSPITALITY';

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Limit Banner */}
      {!dismissedBanner && !isUnlimited && (
        <LimitBanner
          remaining={remaining}
          limit={limit}
          tier={tier}
          nextTierInfo={nextTierInfo || undefined}
          onUpgrade={() => setUpgradeModal({ open: true, attemptedAdd: 1 })}
          onDismiss={() => setDismissedBanner(true)}
          className="mb-6"
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manual NIB Entry</h1>
          <p className="text-sm text-gray-500 mt-1">
            {submission.is_draft ? (
              <span className="inline-flex items-center gap-1">
                <svg
                  className="w-4 h-4 text-yellow-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Draft
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <svg
                  className="w-4 h-4 text-green-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Completed
              </span>
            )}{' '}
            • {submission.format_type}
            {isSaving && (
              <span className="inline-flex items-center gap-1 ml-2 text-blue-600">
                <svg
                  className="animate-spin w-3 h-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-3">
          {submission.is_draft && (
            <button
              onClick={() => navigate('/nib-c10/manual-entry')}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              New Submission
            </button>
          )}
        </div>
      </div>

      {/* Employer Info (read-only once created) */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Employer NI:</span>{' '}
            <span className="font-medium text-gray-900">
              {submission.employer_number}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Company:</span>{' '}
            <span className="font-medium text-gray-900">
              {submission.company_name}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Period:</span>{' '}
            <span className="font-medium text-gray-900">
              {submission.contribution_period}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Employee Section */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-gray-900">Employees</h2>

              <LimitIndicator
                currentCount={employeeCount}
                limit={limit}
                onUpgradeClick={() =>
                  setUpgradeModal({ open: true, attemptedAdd: 1 })
                }
                isLoading={limitLoading}
                size="sm"
              />
            </div>

            {submission.is_draft && (
              <div className="flex gap-2">
                <button
                  onClick={() => setProfileModalOpen(true)}
                  className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg
                    className="w-4 h-4 inline mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  From Profile
                </button>
                <button
                  onClick={handleAddEmployeeClick}
                  disabled={!canAddEmployee && !isUnlimited}
                  className={`
                    px-3 py-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                    ${
                      !canAddEmployee && !isUnlimited
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }
                  `}
                >
                  + Add Employee
                </button>
              </div>
            )}
          </div>

          {(submission.employees?.length ?? 0) === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No employees yet
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first employee
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setEmployeeModalOpen(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Add First Employee
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {(submission.employees ?? []).map(emp => (
                <EmployeeEntryCard
                  key={emp.id}
                  employee={emp}
                  isHospitality={isHospitality}
                  onEdit={handleEditEmployee}
                  onRemove={handleRemoveEmployee}
                  isRemoving={removeEmployee.isPending}
                />
              ))}
            </div>
          )}
        </div>

        {/* Summary Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-6">
            <ContributionSummary
              summary={submission.summary}
              isHospitality={isHospitality}
            />

            {/* Actions */}
            {(submission.employees?.length ?? 0) > 0 && submission.is_draft && (
              <div className="mt-4">
                <button
                  onClick={handleGenerateC10}
                  disabled={generateC10.isPending || !submission.is_valid}
                  className={`
                    w-full px-6 py-3 bg-green-600 text-white rounded-lg font-medium
                    hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed
                    flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500
                  `}
                >
                  {generateC10.isPending ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      Generate C10 File
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Validation Errors */}
            {!submission.is_valid &&
              (submission.validation_errors?.length ?? 0) > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h3 className="font-medium text-red-700 mb-2 text-sm">
                    Validation Errors
                  </h3>
                  <ul className="list-disc list-inside text-xs text-red-600 space-y-1">
                    {submission.validation_errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddEmployeeModal
        isOpen={isEmployeeModalOpen}
        onClose={() => {
          setEmployeeModalOpen(false);
          setEditingEmployee(null);
        }}
        onSubmit={handleAddEmployee}
        isLoading={addEmployee.isPending || updateEmployee.isPending}
        isHospitality={isHospitality}
        editingEmployee={editingEmployee}
      />

      <ProfileSelectorModal
        isOpen={isProfileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
          setSelectedProfiles([]);
        }}
        onSelect={handleSelectProfile}
        selectedIds={selectedProfiles}
        onSelectionChange={setSelectedProfiles}
        maxSelectable={maxSelectableProfiles}
        onConfirmBulk={handleAddFromProfiles}
        isBulkLoading={bulkAddEmployees.isPending}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, attemptedAdd: 0 })}
        currentTier={tier}
        onSuccess={() => setUpgradeModal({ open: false, attemptedAdd: 0 })}
        limitContext={{
          currentCount: employeeCount,
          attemptedAdd: upgradeModal.attemptedAdd,
        }}
      />
    </div>
  );
}
